import { useRef, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import type { UsageSummary } from "@markeroo/shared";
import { zIndex } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { Pill } from "@/components/Pill";
import { ui, plural, remaining, toolbox } from "@/strings";
import { isDevOrStaging } from "@/lib/colorOverrides";

function formatSubscriptionEndDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

interface AccountMenuProps {
  user: { email: string };
  usage: UsageSummary | null;
  isProcessingPortal: boolean;
  isSuperuser?: boolean;
  isInternal?: boolean;
  onPlanAction: () => void;
  onEmailTemplates?: () => void;
  onSignOut: () => void;
}

export function AccountMenu({
  user,
  usage,
  isProcessingPortal,
  isSuperuser = false,
  isInternal = false,
  onPlanAction,
  onEmailTemplates,
  onSignOut,
}: AccountMenuProps) {
  const [isAccountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  const accountButtonLabel = user?.email ?? ui.headers.account;

  const isCanceled = usage?.subscriptionStatus === "canceled";
  const planActionLabel = useMemo(() => {
    if (isProcessingPortal) {
      return ui.loading.default;
    }
    if (isCanceled) {
      return ui.billing.resubscribe;
    }
    if (usage?.hasActiveSubscription) {
      return ui.actions.manageBilling;
    }
    return ui.actions.upgrade;
  }, [isProcessingPortal, isCanceled, usage?.hasActiveSubscription]);

  const progress = usage ? Math.min(Math.round(usage.usagePercent), 100) : 0;
  const isUnlimited = usage?.docsLimit === -1;
  const limitLabel = plural(usage?.docsLimit ?? 0, ui.labels.document, ui.labels.documents);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target as Node) &&
        accountButtonRef.current &&
        !accountButtonRef.current.contains(event.target as Node)
      ) {
        setAccountMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <button
        type="button"
        ref={accountButtonRef}
        className={`${buttonVariants.secondary} items-center gap-2`}
        aria-haspopup="menu"
        aria-expanded={isAccountMenuOpen}
        onClick={() => setAccountMenuOpen((previous) => !previous)}
      >
        <span>{accountButtonLabel}</span>
        {/* eslint-disable-next-line no-restricted-syntax */}
        <span aria-hidden="true">▾</span>
      </button>
      {isAccountMenuOpen ? (
        <div
          ref={accountMenuRef}
          role="menu"
          aria-label={ui.headers.accountMenu}
          className="absolute right-0 mt-2 w-72 rounded-2xl bg-white p-2 shadow-card"
          style={{ zIndex: zIndex.dropdown }}
        >
          {usage ? (
            <div className="px-4 py-3 border-b border-theme-primary">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className="text-sm font-semibold text-brand-950">{ui.headers.usage}</h3>
                <Pill variant="info">{usage.planLabel}</Pill>
              </div>
              <div className="mb-3">
                {!isUnlimited && (
                  <div className="h-1.5 w-full rounded-full bg-surface-secondary mb-2">
                    <div
                      className="h-full rounded-full bg-brand-600 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
                <p className="text-xs text-theme-secondary">
                  {isUnlimited
                    ? ui.pricing.features.papersPerMonth
                    : `${usage.docsUsed} ${ui.labels.of} ${usage.docsLimit} ${limitLabel}`}
                </p>
              </div>
              {usage.overLimit && (
                <p className="text-xs text-semantic-error font-medium mb-2">
                  {ui.labels.limitReached}
                </p>
              )}
              {!usage.overLimit && usage.nearLimit && usage.docsRemaining > 0 && (
                <p className="text-xs text-semantic-warning font-medium mb-2">
                  {remaining(usage.docsRemaining)}
                </p>
              )}
              {isCanceled && usage.currentPeriodEnd && (
                <p className="text-xs text-semantic-error font-medium mb-2">
                  {ui.billing.subscriptionEndsOn(formatSubscriptionEndDate(usage.currentPeriodEnd))}
                </p>
              )}
              {!isInternal && (!usage.hasActiveSubscription || isCanceled) && (
                <button
                  type="button"
                  className={`${buttonVariants.secondary} w-full text-xs py-1.5`}
                  onClick={onPlanAction}
                  disabled={isProcessingPortal}
                >
                  {planActionLabel}
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col rounded-xl px-4 py-3 text-sm text-theme-muted border-b border-theme-primary" role="menuitem">
              {ui.loading.usage}
            </div>
          )}
          <div className="pt-2">
            <Link
              role="menuitem"
              className="flex w-full items-center rounded-xl px-4 py-2 text-left text-sm text-theme-secondary transition hover:bg-surface-secondary"
              to="/settings"
              onClick={() => setAccountMenuOpen(false)}
            >
              {ui.headers.settings}
            </Link>
            {onEmailTemplates && (
              <button
                role="menuitem"
                type="button"
                className="flex w-full items-center rounded-xl px-4 py-2 text-left text-sm text-theme-secondary transition hover:bg-surface-secondary"
                onClick={() => {
                  setAccountMenuOpen(false);
                  onEmailTemplates();
                }}
              >
                {ui.headers.emailTemplates}
              </button>
            )}
            {isSuperuser && (
              <Link
                role="menuitem"
                className="flex w-full items-center rounded-xl px-4 py-2 text-left text-sm text-theme-secondary transition hover:bg-surface-secondary"
                to="/admin/users"
                onClick={() => setAccountMenuOpen(false)}
              >
                {ui.headers.adminConsole}
              </Link>
            )}
            {isInternal && isDevOrStaging() && (
              <Link
                role="menuitem"
                className="flex w-full items-center rounded-xl px-4 py-2 text-left text-sm text-theme-secondary transition hover:bg-surface-secondary"
                to="/toolbox"
                onClick={() => setAccountMenuOpen(false)}
              >
                {toolbox.pageTitle}
              </Link>
            )}
            <button
              role="menuitem"
              type="button"
              className="flex w-full items-center rounded-xl px-4 py-2 text-left text-sm text-theme-secondary transition hover:bg-surface-secondary"
              onClick={onSignOut}
            >
              {ui.actions.signOut}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
